<template>
  <main class="section details" aria-labelledby="product-title">
    <div class="container" v-if="product">
      <button type="button" class="back" @click="goBack">← {{ t('details.back') }}</button>
      <figure class="hero">
        <img class="hero-img" :src="product.image" :alt="pTitle(product)" />
      </figure>
      <h1 id="product-title" class="title">{{ pTitle(product) }}</h1>
      <p class="desc">{{ pDesc(product) }}</p>
      <div class="doc-actions" role="group" aria-label="Document actions">
        <button class="doc-btn preview" type="button" @click="onPreview(product)">{{ t('details.preview') }}</button>
        <button class="doc-btn download" type="button" @click="onDownload(product)">{{ t('details.downloadPdf') }}</button>
      </div>
    </div>
    <div class="container" v-else>
      <button type="button" class="back" @click="goBack">← {{ t('details.back') }}</button>
      <h1 class="title">{{ t('details.notFound') }}</h1>
    </div>
  </main>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { products } from '../data/products'
import { previewProductPdf, downloadProductPdf } from '../utils/pdf'

const route = useRoute()
const router = useRouter()
const { t, locale } = useI18n()
const product = computed(() => products.find(p => p.id === route.params.id))

const pTitle = (p) => p[locale.value === 'zh-CN' ? 'zh' : locale.value]?.title || p.en.title
const pDesc = (p) => p[locale.value === 'zh-CN' ? 'zh' : locale.value]?.desc || p.en.desc

async function onPreview(p) { await previewProductPdf(p, locale.value) }
async function onDownload(p) { await downloadProductPdf(p, locale.value) }

function goBack() {
  // If there is a previous history entry, go back. Otherwise, go to home products section.
  if (window.history.length > 1) {
    router.back()
  } else {
    router.push({ path: '/', hash: '#products' })
  }
}
</script>

<style scoped>
.section.details {
  padding: 3.5rem 1.5rem 4rem;
  background: radial-gradient(1200px 600px at 10% -20%, rgba(96,165,250,0.12), transparent),
              radial-gradient(900px 500px at 110% 120%, rgba(37,99,235,0.14), transparent),
              linear-gradient(180deg, rgba(6,10,22,1) 0%, rgba(10,14,28,1) 100%);
}
.container { max-width: 900px; margin: 0 auto; }
.back { display: inline-block; margin-bottom: 1rem; color: #cde0ff; text-decoration: none; background: none; border: none; padding: 0; cursor: pointer; font: inherit; }
.back:hover { text-decoration: underline; }
.title { color: #e6f0ff; font-size: clamp(1.6rem, 4vw, 2.2rem); margin-bottom: .5rem; }
.desc { color: #b7c5ff; font-size: 1.05rem; }

.doc-actions { display: flex; gap: .6rem; margin-top: 1rem; flex-wrap: wrap; }
.doc-btn {
  position: relative; isolation: isolate;
  display: inline-flex; align-items: center; justify-content: center;
  padding: .65rem 1rem; border-radius: 12px; border: 1px solid rgba(255,255,255,0.12);
  color: #eaf1ff; background: rgba(255,255,255,0.06); cursor: pointer; font: inherit;
  overflow: hidden;
}
.doc-btn::before {
  content: ''; position: absolute; inset: -40%; z-index: -1; pointer-events: none;
  background: radial-gradient(60% 80% at 20% 30%, rgba(96,165,250,.35), transparent 60%),
              radial-gradient(60% 80% at 80% 70%, rgba(37,99,235,.35), transparent 60%);
  opacity: .0; transform: scale(1);
  transition: opacity .25s ease, transform .25s ease;
}
.doc-btn:hover::before { opacity: .9; transform: scale(1.02); }
.doc-btn.preview { border-color: rgba(96,165,250,.40); }
.doc-btn.download { border-color: rgba(37,99,235,.40); }
@media (prefers-reduced-motion: no-preference) {
  .doc-btn { transition: transform .2s ease, box-shadow .2s ease, filter .2s ease; box-shadow: 0 8px 22px rgba(0,0,0,.22); }
  .doc-btn:hover { transform: translateY(-2px); filter: brightness(1.05); box-shadow: 0 12px 28px rgba(0,0,0,.3); }
  .doc-btn.download { animation: subtlePulse 5s ease-in-out infinite; }
}
@keyframes subtlePulse { 0%,100%{ box-shadow: 0 8px 22px rgba(0,0,0,.22) } 50%{ box-shadow: 0 12px 28px rgba(0,0,0,.30) } }

.hero { margin: 0 0 1rem; border-radius: 16px; overflow: hidden; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); }
.hero-img { width: 100%; height: auto; display: block; aspect-ratio: 16 / 9; object-fit: cover; filter: saturate(1.15) contrast(1.05); }
</style>
